import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion } from "framer-motion";
import { ClockIcon, QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { useWindowSize } from "@utils/hooks";
import { ShowingStatus, AccessType } from "@utils/enums";
import { joinClassNames } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import { Button, Card, Heading, Text, Tooltip } from "@components/theming";
import DialogCloseButton from "@components/popovers/dialogCloseButton";

interface Props {
  title: string;
  description: string;
  statusId: number;
  accessType: string;
}

export default function ShowingStatusToolTip({
  title,
  description,
  statusId,
  accessType,
}: Props) {
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const t = useTranslations("Utils");
  const isShopsite = useIsShopsite();

  const status_colours = {
    [ShowingStatus.SOLDOUT]: "red",
    [ShowingStatus.ADMINSOLDOUT]: "red",
    [ShowingStatus.CANCELLED]: "red",
    [ShowingStatus.POSTPONED]: "orange",
    [ShowingStatus.NOTYETONSALE]: "orange",
    [ShowingStatus.FREEENTRY]: "green",
  } as const;

  const access_colours = {
    [AccessType.SALESCLOSED]: "red",
    [AccessType.NOTONSALE]: "red",
    [AccessType.DOORSALES]: "orange",
    [AccessType.NOTYETONSALE]: "orange",
    [AccessType.EXPIRED]: "red",
  } as const;

  const tooltip_colour =
    statusId === ShowingStatus.AVAILABLE
      ? access_colours[accessType as keyof typeof access_colours]
      : status_colours[statusId as keyof typeof status_colours];

  const shopsiteColor = tooltip_colour ?? "indigo";

  const bgColor = `bg-${shopsiteColor}-100`;
  const iconColor = `text-${shopsiteColor}-600`;
  const dialogBgColor = `bg-${shopsiteColor}-500`;
  const dialogCloseHover = `bg-${shopsiteColor}-400`;
  const dialogDisabled = `bg-${shopsiteColor}-200`;
  const tooltipContent = `text-${shopsiteColor}-800`;
  const tooltipArrow = `fill-${shopsiteColor}-100`;

  return windowSize.width && windowSize.width > 640 ? (
    <Tooltip
      side="bottom"
      sideOffset={10}
      content={description}
      shopsiteClasses={joinClassNames(
        "text-sm max-w-96",
        tooltipContent,
        bgColor
      )}
      arrowClasses={tooltipArrow}
    >
      <Text
        aria-label={`${title}-button`}
        shopsiteClasses={iconColor}
        whitelabelClasses={`text-[color:var(--${
          tooltip_colour ?? "accent"
        }-a11)]`}
      >
        <QuestionMarkCircleIcon className="w-5 h-5" />
      </Text>
    </Tooltip>
  ) : (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Text
          whitelabelClasses={`text-[color:var(--${
            tooltip_colour ?? "accent"
          }-a11)]`}
          shopsiteClasses={iconColor}
        >
          <QuestionMarkCircleIcon className="w-5 h-5" />
        </Text>
      </DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent asChild forceMount handleClose={() => setOpen(false)}>
            <Card asChild>
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                transition={{ duration: 0.2 }}
                variants={{
                  hidden: {
                    y: "100%",
                    opacity: 0,
                    backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
                  },
                  show: {
                    y: 0,
                    opacity: 1,
                    backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
                  },
                }}
              >
                <div className="flex flex-col">
                  <div
                    className={joinClassNames(
                      "flex flex-col gap-4 pb-4",
                      isShopsite ? "px-6 pt-6" : "",
                      isShopsite ? dialogBgColor : ""
                    )}
                  >
                    <div className="relative self-end">
                      <DialogClose asChild>
                        <DialogCloseButton shopsiteVariant="white" />
                      </DialogClose>
                    </div>
                    <DialogHeader className="!text-left">
                      <DialogTitle asChild>
                        <Heading
                          as="h3"
                          size="5"
                          weight="bold"
                          shopsiteClasses="text-white text-xl font-bold"
                        >
                          {title}
                        </Heading>
                      </DialogTitle>
                      <ClockIcon
                        className={joinClassNames(
                          "absolute top-0 left-4 w-24 h-24 -rotate-12",
                          isShopsite
                            ? "text-white/10"
                            : tooltip_colour === "red"
                            ? "text-[color:var(--red-a3)]"
                            : tooltip_colour === "orange"
                            ? "text-[color:var(--orange-a3)]"
                            : "text-[color:var(--accent-a3)]"
                        )}
                      />
                    </DialogHeader>
                  </div>
                  <Text color="gray" shopsiteClasses="px-6 py-4 text-gray-600">
                    {description}
                  </Text>
                  <div className={isShopsite ? "px-6 pb-6" : ""}>
                    <DialogClose
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      asChild
                    >
                      <Button
                        color={tooltip_colour}
                        size="3"
                        mt="1rem"
                        shopsiteClasses={joinClassNames(
                          "w-full",
                          dialogBgColor,
                          dialogCloseHover,
                          dialogDisabled
                        )}
                      >
                        {t("done")}
                      </Button>
                    </DialogClose>
                  </div>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
